import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../config/api';
import { getDecryptedData, getUserType } from '../utils/authStorage';
import { useLayout } from '../layout/AppLayout';

const REMINDER_INTERVAL_MS = Number(process.env.REACT_APP_PRIORITY_REMINDER_INTERVAL_MS)
  || (2 * 60 * 60 * 1000);

export function usePriorityProfileReminder() {
  const userType = getUserType();
  const userId = getDecryptedData('Id');
  const { tableRefresh } = useLayout();
  const [shouldRemind, setShouldRemind] = useState(false);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const checkReminder = useCallback(async () => {
    if (!userId || !['User', 'TeamLead'].includes(userType)) {
      setShouldRemind(false);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(`/api/priority-profile-reminder/${userId}`);
      const needsReminder = Boolean(response.data?.shouldRemind);
      setShouldRemind(needsReminder);
    } catch (error) {
      setShouldRemind(false);
    } finally {
      setLoading(false);
    }
  }, [userId, userType]);

  useEffect(() => {
    checkReminder();

    intervalRef.current = window.setInterval(() => {
      checkReminder();
    }, REMINDER_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [checkReminder]);

  useEffect(() => {
    if (!userId) return;
    checkReminder();
  }, [tableRefresh.requirements, tableRefresh.candidates, checkReminder, userId]);

  const visible = shouldRemind && !loading;

  return {
    visible,
    loading,
    refresh: checkReminder,
  };
}

export default usePriorityProfileReminder;
